import React, { useEffect, useState } from "react";
import { api, STAGES } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const PERM_LABELS = {
  view: "View", add: "Add", edit: "Edit", reassign: "Reassign", archive: "Archive", restore: "Restore", delete: "Permanently delete",
  docs: "Upload / download documents", import_export: "Import / export", finance_view: "View finance", finance_edit: "Edit finance", manage_settings: "Manage settings",
};
const BASIC = ["view", "add", "edit", "reassign", "docs", "finance_view"];
const ROLE_LABEL = r => r.replace(/_/g, " ");
const FIELD_LABEL = { course_name: "Course name", intake_id: "Intake", campus: "Campus", college_app_id: "College application ID" };

export default function B2BDeptSettings({ recipients }) {
  const [d, setD] = useState(null);
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [audit, setAudit] = useState([]);
  const load = () => {
    api.get("/settings/departments/b2b").then(r => { setD(r.data); setS(JSON.parse(JSON.stringify(r.data.settings))); }).catch(e => setErr(e.response?.data?.detail || "Could not load"));
    api.get("/settings/audit").then(r => setAudit(r.data)).catch(() => {});
  };
  useEffect(load, []);
  if (err) return <div data-testid="b2b-settings-denied" className="bg-white p-6 rounded-xl border border-slate-200 text-sm text-slate-600">{err}</div>;
  if (!s) return <div className="text-sm text-slate-500">Loading B2B settings…</div>;
  const save = async () => {
    try { await api.put("/settings/departments/b2b", s); toast.success("B2B settings saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const setRole = (role, fn) => setS(x => ({ ...x, roles: { ...x.roles, [role]: fn(x.roles[role]) } }));
  const officers = d.members.filter(m => m.role === "admissions_officer" && m.is_active !== false);
  const bdms = d.members.filter(m => m.role === "bdm" && m.is_active !== false);

  return (
    <div data-testid="b2b-settings" className="space-y-4">
      <Section n="1" title="Team & Access" hint="Who is in B2B, what each role may do, and which records they see. Enforced on the server, in reports, exports and file downloads.">
        <div className="flex flex-wrap gap-1.5 text-xs">{d.members.map(m => <span key={m.user_id} className={`rounded-full border px-2.5 py-0.5 ${m.is_active === false ? "opacity-50" : ""}`}>{m.name} · {ROLE_LABEL(m.role)}</span>)}</div>
        <PermTable s={s} d={d} keys={BASIC} setRole={setRole} />
        <details><summary className="text-xs text-slate-600 cursor-pointer" data-testid="b2b-perm-advanced">Advanced permissions</summary>
          <PermTable s={s} d={d} keys={d.perm_keys.filter(k => !BASIC.includes(k))} setRole={setRole} hideVis />
          <Button size="sm" variant="outline" className="mt-2" data-testid="b2b-perm-reset" onClick={() => setS(x => ({ ...x, roles: JSON.parse(JSON.stringify(d.presets)) }))}>Reset all roles to presets</Button>
        </details>
      </Section>
      <Section n="2" title="Fields & Lists" hint="Program Category is one shared list for all colleges, B2B and B2C. Course Name is free text.">
        <div className="flex flex-wrap gap-1.5">{d.categories.map(c => <span key={c} className="rounded-md bg-orange-50 border border-orange-200 text-orange-900 text-xs px-2 py-1">{c}</span>)}</div>
        <div className="text-xs text-slate-600 pt-1">Required on new applications (besides student, college and category):</div>
        <div className="flex flex-wrap gap-3 text-sm">
          {d.required_choices.map(f => (
            <label key={f} className="inline-flex items-center gap-1.5"><input type="checkbox" data-testid={`b2b-required-${f}`} checked={s.required_fields.includes(f)}
              onChange={e => setS(x => ({ ...x, required_fields: e.target.checked ? [...x.required_fields, f] : x.required_fields.filter(y => y !== f) }))} />{FIELD_LABEL[f]}</label>
          ))}
        </div>
      </Section>
      <Section n="3" title="Workflow" hint="Stages belong to each application. Key rules: Visa Applied needs Deposit Paid first; Backed Out / Withdrawn need a reason.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Pick label="Default admissions officer for new applications" tid="b2b-default-ao" value={s.default_admissions_officer_id} items={officers} onChange={v => setS({ ...s, default_admissions_officer_id: v })} />
          <Pick label="Default BDM for new applications" tid="b2b-default-bdm" value={s.default_bdm_id} items={bdms} onChange={v => setS({ ...s, default_bdm_id: v })} />
        </div>
        <details><summary className="text-xs text-slate-600 cursor-pointer">Stages ({STAGES.length})</summary><div className="flex flex-wrap gap-1 pt-2">{STAGES.map(x => <span key={x} className="text-[11px] rounded bg-slate-100 px-1.5 py-0.5">{x}</span>)}</div></details>
      </Section>
      <Section n="4" title="Reminders" hint="Deadline groups on the B2B dashboard. 'Today' uses the Global timezone. Responsible: the assigned officer.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[["permit_days", "Visa / permit expiry"], ["fee_days", "Fee payment deadlines"]].map(([k, l]) => (
            <div key={k} className="space-y-1"><div className="text-xs text-slate-600">{l}: warn within (days)</div>
              <div className="flex gap-2">{[0, 1].map(i => <Input key={i} type="number" data-testid={`b2b-${k}-${i}`} className="w-24" value={s.reminders[k][i]}
                onChange={e => setS(x => { const r = [...x.reminders[k]]; r[i] = Number(e.target.value); return { ...x, reminders: { ...x.reminders, [k]: r } }; })} />)}</div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-500">Escalation emails come in Phase 3 — nothing is sent from here today.</p>
      </Section>
      <Section n="5" title="Templates & Reports" hint="Report recipients and what the dashboard shows.">
        <div className="flex flex-wrap gap-4 text-sm">
          {[["show_permit_box", "Show Visa / Permit Expiry box"], ["show_fee_box", "Show Fee Payment Deadlines box"]].map(([k, l]) => (
            <label key={k} className="inline-flex items-center gap-1.5"><input type="checkbox" data-testid={`b2b-${k}`} checked={s.dashboard[k]} onChange={e => setS(x => ({ ...x, dashboard: { ...x.dashboard, [k]: e.target.checked } }))} />{l}</label>
          ))}
        </div>
        {recipients}
      </Section>
      <div className="flex items-center gap-3">
        <Button data-testid="b2b-settings-save" onClick={save} className="bg-orange-500 hover:bg-orange-600 text-white">Save B2B settings</Button>
        <span className="text-xs text-slate-500">Every change is recorded below. Managers can't grant more than they have.</span>
      </div>
      {audit.length > 0 && (
        <div data-testid="b2b-settings-audit" className="bg-white rounded-xl border border-slate-200 p-4 text-xs text-slate-600 space-y-1">
          <div className="font-semibold text-slate-800 text-sm">Recent settings changes</div>
          {audit.slice(0, 8).map(a => <div key={a.id}>{new Date(a.at).toLocaleString()} · {a.by} · {a.key} · {a.changed.join(", ") || "no change"}</div>)}
        </div>
      )}
    </div>
  );
}

function PermTable({ s, d, keys, setRole, hideVis }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-slate-500"><th className="text-left py-1 pr-2">Role</th>{!hideVis && <th className="text-left px-2">Sees records</th>}{keys.map(k => <th key={k} className="px-2 font-medium">{PERM_LABELS[k]}</th>)}</tr></thead>
        <tbody>
          {d.roles.map(r => (
            <tr key={r} className="border-t border-slate-100">
              <td className="py-1.5 pr-2 capitalize font-medium text-slate-800">{ROLE_LABEL(r)}</td>
              {!hideVis && <td className="px-2"><select data-testid={`perm-vis-${r}`} className="border rounded px-1 py-0.5" value={s.roles[r].visibility}
                onChange={e => setRole(r, c => ({ ...c, visibility: e.target.value }))}>
                <option value="assigned">Assigned to them</option><option value="team">Team (own + unassigned)</option><option value="all">All department</option></select></td>}
              {keys.map(k => <td key={k} className="px-2 text-center"><input type="checkbox" data-testid={`perm-${r}-${k}`} checked={!!s.roles[r].perms[k]}
                onChange={e => setRole(r, c => ({ ...c, perms: { ...c.perms, [k]: e.target.checked } }))} /></td>)}
            </tr>
          ))}
          <tr className="border-t border-slate-100 text-slate-400"><td className="py-1.5">admin</td><td colSpan={keys.length + 1} className="px-2">Always full access</td></tr>
        </tbody>
      </table>
    </div>
  );
}

const Pick = ({ label, tid, value, items, onChange }) => (
  <div className="space-y-1"><div className="text-xs text-slate-600">{label}</div>
    <Select value={value || "__none__"} onValueChange={v => onChange(v === "__none__" ? null : v)}>
      <SelectTrigger data-testid={tid} className="h-9"><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="__none__">None (auto-pick when only one)</SelectItem>{items.map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const Section = ({ n, title, hint, children }) => (
  <div data-testid={`b2b-section-${n}`} className="bg-white p-5 rounded-xl border border-slate-200 space-y-3">
    <div><div className="heading-font font-semibold text-slate-900"><span className="text-orange-600">{n}.</span> {title}</div><p className="text-xs text-slate-500 mt-0.5">{hint}</p></div>
    {children}
  </div>
);
